/**
 * countries.js — minimal ISO-3166-1 alpha-2 list with display names + emoji flags.
 * Used by the registration form, the Edit Profile dialog, and any admin UI
 * needing a country selector. Kept inline (no extra dep) so first paint
 * isn't blocked on an extra bundle chunk.
 *
 * `COUNTRIES` is exported ALPHABETICALLY by `name` (Feb 2026 fix) so
 * dropdowns look right without every consumer needing to `.sort()` at
 * render time.
 */

const _RAW_COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States',  flag: '🇺🇸' },
  { code: 'NG', name: 'Nigeria',        flag: '🇳🇬' },
  { code: 'CA', name: 'Canada',         flag: '🇨🇦' },
  { code: 'AU', name: 'Australia',      flag: '🇦🇺' },
  { code: 'IE', name: 'Ireland',        flag: '🇮🇪' },
  { code: 'IN', name: 'India',          flag: '🇮🇳' },
  { code: 'ZA', name: 'South Africa',   flag: '🇿🇦' },
  { code: 'KE', name: 'Kenya',          flag: '🇰🇪' },
  { code: 'GH', name: 'Ghana',          flag: '🇬🇭' },
  { code: 'FR', name: 'France',         flag: '🇫🇷' },
  { code: 'DE', name: 'Germany',        flag: '🇩🇪' },
  { code: 'ES', name: 'Spain',          flag: '🇪🇸' },
  { code: 'IT', name: 'Italy',          flag: '🇮🇹' },
  { code: 'NL', name: 'Netherlands',    flag: '🇳🇱' },
  { code: 'SE', name: 'Sweden',         flag: '🇸🇪' },
  { code: 'NO', name: 'Norway',         flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark',        flag: '🇩🇰' },
  { code: 'FI', name: 'Finland',        flag: '🇫🇮' },
  { code: 'BR', name: 'Brazil',         flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico',         flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina',      flag: '🇦🇷' },
  { code: 'CL', name: 'Chile',          flag: '🇨🇱' },
  { code: 'JP', name: 'Japan',          flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea',    flag: '🇰🇷' },
  { code: 'CN', name: 'China',          flag: '🇨🇳' },
  { code: 'SG', name: 'Singapore',      flag: '🇸🇬' },
  { code: 'MY', name: 'Malaysia',       flag: '🇲🇾' },
  { code: 'PH', name: 'Philippines',    flag: '🇵🇭' },
  { code: 'ID', name: 'Indonesia',      flag: '🇮🇩' },
  { code: 'TH', name: 'Thailand',       flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam',        flag: '🇻🇳' },
  { code: 'PK', name: 'Pakistan',       flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh',     flag: '🇧🇩' },
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia',   flag: '🇸🇦' },
  { code: 'TR', name: 'Türkiye',        flag: '🇹🇷' },
  { code: 'EG', name: 'Egypt',          flag: '🇪🇬' },
  { code: 'MA', name: 'Morocco',        flag: '🇲🇦' },
  { code: 'TZ', name: 'Tanzania',       flag: '🇹🇿' },
  { code: 'UG', name: 'Uganda',         flag: '🇺🇬' },
  { code: 'NZ', name: 'New Zealand',    flag: '🇳🇿' },
  { code: 'PT', name: 'Portugal',       flag: '🇵🇹' },
  { code: 'PL', name: 'Poland',         flag: '🇵🇱' },
  { code: 'GR', name: 'Greece',         flag: '🇬🇷' },
  { code: 'CH', name: 'Switzerland',    flag: '🇨🇭' },
  { code: 'AT', name: 'Austria',        flag: '🇦🇹' },
  { code: 'BE', name: 'Belgium',        flag: '🇧🇪' },
  { code: 'RU', name: 'Russia',         flag: '🇷🇺' },
  { code: 'UA', name: 'Ukraine',        flag: '🇺🇦' },
  { code: 'IL', name: 'Israel',         flag: '🇮🇱' },
];

export const COUNTRIES = [..._RAW_COUNTRIES].sort((a, b) =>
  a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
);

const CODE_TO_COUNTRY = Object.fromEntries(COUNTRIES.map((c) => [c.code, c]));

/** Try to derive an ISO-2 from the browser. Falls back to null. */
export const detectBrowserCountry = () => {
  try {
    // navigator.language returns "en-GB", "en-US", etc.
    const lang = (navigator.language || navigator.userLanguage || '').trim();
    if (lang.includes('-')) {
      // Strip anything that isn't A-Z so locales like "en-US@posix" still
      // resolve to "US" instead of "US@POSIX".
      const code = lang.split('-').pop().toUpperCase().replace(/[^A-Z]/g, '');
      if (CODE_TO_COUNTRY[code]) return code;
    }
    // Last resort — peek at the timezone for a UK/US hint.
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    if (tz.startsWith('europe/london')) return 'GB';
    if (tz.startsWith('america/')) return 'US';
  } catch { /* ignore */ }
  return null;
};

export const labelForCountry = (code) => {
  if (!code) return '—';
  const meta = CODE_TO_COUNTRY[code.toUpperCase()];
  if (!meta) return code.toUpperCase();
  return `${meta.flag} ${meta.name}`;
};
